import axios from "axios";
import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Input,
    Badge,
    Spinner,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ListGroup,
    ListGroupItem,
    Toast,
    ToastBody,
    ToastHeader,
    Nav,
    NavItem,
    NavLink,
    Alert
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import { SearchResult } from "./Interfaces";

interface Stock {
    entrepot_id: string;
    entrepot_name: string;
    stock: string;
}

interface SupplierOrderLine {
    id: string;
    product_id: string;
    ref: string;
    product_ref: string;
    desc: string;
    description: string;
    product_label: string;
    qty: string;
    subprice: string;
    total_ht: string;
    total_ttc: string;
    total_tva: string;
    tva_tx: string;
    pu_ht: string;
    rang: string;
    array_options: {
        options_origin: string;
        options_commandeorigine: string | null;
    };
}

interface ExpeditionLine {
    id: string;
    product_id: string;
    desc: string;
    libelle: string;
    qty: string;
    qty_shipped: string;
    subprice: string;
    ref: string;
    tva_tx: string;
    comment: string;
    stock: Stock[];
    commandefourndet?: string;
    fournisseurs?: SupplierOrderResponse | { [key: string]: { idwh: string; stock: number; warehouse_name: string, price: number } };
    supplierOrderData?: SupplierOrderData & {
        commandeFournId?: string;
        entity?: number;
        entityname?: string;
        message?: string;
        success?: boolean;
    };
}

interface SupplierOrderData {
    id: string;
    ref: string;
    statut: string;
    date: string;
    date_validation: string | null;
    total_ht: string;
    total_ttc: string;
    total_tva: string;
    fourn_id: string;
    socid: string;
    entity: string;
    entityname: string;
    user_author_id: string;
    lines: SupplierOrderLine[];
    ref_ext?: string;
    ref_supplier?: string;
    mode_reglement?: string;
    cond_reglement?: string;
    note?: string;
    note_public?: string;
    note_private?: string;
    model_pdf?: string;
    brouillon?: number;
    billed?: string;
    remise_percent?: string;
    multicurrency_code?: string;
    multicurrency_total_ht?: string;
    multicurrency_total_ttc?: string;
    multicurrency_total_tva?: string;
    picto?: string;
    status?: string;
    fields?: any;
    linked_objects?: any[];
    comments?: any[];
    commandeFournId?: string;
    message?: string;
    success?: boolean;
    thirdparty?: {
        name: string
    }
}

interface SupplierOrderResponse {
    success: boolean;
    message: string;
    data?: SupplierOrderData;
    commandeFournId?: string;
    entity?: number;
    entityname?: string;
}

interface ExpeditionData {
    id: string;
    ref: string;
    status: string;
    orderinfo: {
        orderid: number;
        ref: string;
        client_name: string;
        created_by: string;
        date_creation: string;
        date_livraison: string;
        validation: string;
    };
    lines: ExpeditionLine[];
}

interface SupplierInfo {
    idwh: string;
    stock: number;
    price: number;
    warehouse_name: string;
    supplier_name?: string;
}

interface ToastMessage {
    id: number;
    title: string;
    message: string;
    color: 'success' | 'danger' | 'warning' | 'info';
}

interface ValidationResponse {
    success: boolean;
    message: string;
    order_validation?: string;
    closed?: number;
    resultPDF?: string;
}

const Reparation = () => {
    const { user, order, changeOrder } = useAuth();
    const [comptoirOrders, setComptoirOrders] = useState<SearchResult[]>([]);
    const [franchisesOrders, setFranchisesOrders] = useState<SearchResult[]>([]);
    const [preparedQuantities, setPreparedQuantities] = useState<Record<string, number>>({});
    const [selectedWarehouses, setSelectedWarehouses] = useState<Record<string, string>>({});
    const [expeditionData, setExpeditionData] = useState<ExpeditionData | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [expeditionId, setExpeditionId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [createdOrderRef, setCreatedOrderRef] = useState("");

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [modalType, setModalType] = useState<"commander" | "voir">("commander");
    const [selectedLine, setSelectedLine] = useState<ExpeditionLine | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [fournisseurId, setFournisseurId] = useState<string>("");
    const [availableSuppliers, setAvailableSuppliers] = useState<SupplierInfo[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<string>("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);
    const [validationResponse, setValidationResponse] = useState<ValidationResponse | null>(null);

    // Toast notifications state
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    // Tab state
    const [activeTab, setActiveTab] = useState<'comptoir' | 'franchises'>('comptoir');
    const [allOrders, setAllOrders] = useState<SearchResult[]>([]);

    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfFileName, setPdfFileName] = useState<string>("");
    const [pdfUrl, setPdfUrl] = useState<string>("");

    // État pour savoir si la commande actuelle est une franchise
    const [isFranchiseOrder, setIsFranchiseOrder] = useState(false);

    // Nouvel état pour le modal des problèmes de quantité
    const [showQuantityIssueModal, setShowQuantityIssueModal] = useState(false);

    // Add toast notification
    const addToast = (title: string, message: string, color: 'success' | 'danger' | 'warning' | 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, title, message, color }]);

        // Auto remove toast after 5 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // Remove toast
    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const toggleDropdown = (lineId: string) => {
        setDropdownOpen(prev => ({
            ...prev,
            [lineId]: !prev[lineId]
        }));
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setFournisseurId("");
            setAvailableSuppliers([]);
            setSelectedSupplier("");
        }
    };

    // Fonction pour vérifier si toutes les lignes sont complètes
    const checkAllLinesComplete = () => {
        if (!expeditionData) return true;
        
        return expeditionData.lines.every(line => {
            const preparedQty = preparedQuantities[line.id] || 0;
            const orderedQty = parseInt(line.qty);
            return preparedQty >= orderedQty;
        });
    };

    const openCommanderModal = (line: ExpeditionLine) => {
        setSelectedLine(line);
        setModalType("commander");
        setModalTitle("Commander le produit");
        setModalOpen(true);
        setModalLoading(false);

        if (line.fournisseurs && Object.keys(line.fournisseurs).length > 0) {
            const suppliers: SupplierInfo[] = Object.entries(line.fournisseurs).map(([supplierName, data]) => ({
                idwh: data.idwh,
                stock: data.stock,
                price: data.price,
                warehouse_name: data.warehouse_name,
                supplier_name: supplierName
            }));
            setAvailableSuppliers(suppliers);
            if (suppliers.length > 0) {
                setSelectedSupplier(suppliers[0].idwh);
                setModalMessage(`Sélectionnez un fournisseur pour: ${line.ref} - ${line.desc}`);
            } else {
                setModalMessage(`Aucun fournisseur disponible pour ce produit.`);
            }
        } else {
            setModalMessage(`Aucun fournisseur disponible pour ce produit.`);
            setAvailableSuppliers([]);
        }
    };

    const openVoirModal = async (line: ExpeditionLine) => {
        setSelectedLine(line);
        setModalType("voir");
        setModalTitle("Détails de la commande fournisseur");
        setModalOpen(true);
        setModalLoading(true);

        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'getcommandefourn',
                    commandefourndet: parseInt(line.commandefourndet || '0'),
                    entity: user.entity
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data) {
                setSelectedLine(prev => prev ? {
                    ...prev,
                    supplierOrderData: response.data
                } : prev);
                setModalMessage(`Commande fournisseur: ${response.data.ref}`);
            } else {
                setModalMessage(`Aucune commande fournisseur trouvée pour ce produit.`);
            }
        } catch (error) {
            console.error("Error fetching supplier order details:", error);
            setModalMessage("Erreur lors de la récupération des détails du fournisseur.");
        } finally {
            setModalLoading(false);
        }
    };

    const handleCreateSupplierOrder = async () => {
        if (!selectedLine || !selectedSupplier) {
            addToast("Erreur", "Veuillez sélectionner un fournisseur", "warning");
            return;
        }

        setModalLoading(true);
        const data = {
            action: 'setcommandefourn',
            exp_line_id: parseInt(selectedLine.id),
            fournisseur: selectedSupplier,
            entity: user.entity
        };

        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data) {
                setCreatedOrderRef(response.data.ref);
                setSuccessModalOpen(true);
                addToast("Succès", "Commande fournisseur créée avec succès", "success");

                if (order) {
                    await fetchExpeditionDetails(parseInt(order.id));
                }
                toggleModal();
            } else {
                addToast("Erreur", "Erreur lors de la création de la commande fournisseur", "danger");
            }
        } catch (error) {
            console.error('Error creating supplier order:', error);
            addToast("Erreur", "Erreur lors de la création de la commande fournisseur", "danger");
        } finally {
            setModalLoading(false);
        }
    };

    const fetchCommandes = async () => {
        try {
            const response = await axios.post(
                "/apiV2/",
                { action: "toprepare", entity: user.entity },
                {
                    baseURL: "https://api.apapiecesauto.fr",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`,
                    },
                }
            );

            const comptoirList: SearchResult[] = response.data.comptoir || [];
            const franchisesList: SearchResult[] = response.data.franchises || [];

            setComptoirOrders(comptoirList);
            setFranchisesOrders(franchisesList);

            // Set all orders based on active tab
            const currentOrders = activeTab === 'comptoir' ? comptoirList : franchisesList;
            setAllOrders(currentOrders);
            console.log(comptoirList);
            console.log(franchisesList);
        } catch (error) {
            console.error("Error fetching orders:", error);
            addToast("Erreur", "Erreur lors du chargement des commandes", "danger");
        }
    };
  
    const fetchExpeditionDetails = async (orderId: number) => {
        setDetailsLoading(true);
        const data = {
            action: 'getexpedition',
            orderid: orderId,
            entity: user.entity
        }
        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );
            //debugger;
            if (response.data && response.data.length > 0) {
                const expedition = response.data[0];

                const mappedExpedition: ExpeditionData = {
                    id: expedition.id,
                    ref: expedition.ref,
                    orderinfo: {
                        orderid: expedition.orderinfo.orderid,
                        ref: expedition.orderinfo.ref,
                        client_name: expedition.orderinfo.client_name,
                        created_by: expedition.orderinfo.created_by,
                        date_creation: expedition.orderinfo.date_creation,
                        date_livraison: expedition.orderinfo.date_livraison,
                        validation: expedition.orderinfo.validation
                    },
                    status: expedition.status,
                    lines: expedition.lines.map((line: any) => ({
                        id: line.id,
                        product_id: line.product_id,
                        ref: line.ref || line.product_ref || '',
                        desc: line.desc,
                        qty: line.qty,
                        qty_shipped: line.qty_shipped,
                        subprice: line.subprice,
                        tva_tx: line.tva_tx,
                        stock: line.stock || [],
                        comment: line.comment || "",
                        commandefourndet: line.commandefourndet,
                        fournisseurs: line.fournisseurs
                    }))
                };

                setExpeditionData(mappedExpedition);
                setExpeditionId(expedition.ref);
                console.log("Expedition Details:", mappedExpedition);
                // Vérifier si c'est une commande franchise
                const franchiseOrder = franchisesOrders.find(fOrder => fOrder.id === order?.id);

                const initialQuantities: Record<string, number> = {};
                const initialWarehouses: Record<string, string> = {};

                if (mappedExpedition.lines) {
                    mappedExpedition.lines.forEach((line: ExpeditionLine) => {
                        // Initialize with qty_shipped (already prepared quantity)
                        const alreadyPrepared = parseInt(line.qty_shipped) || 0;
                        initialQuantities[line.id] = alreadyPrepared;

                        if (line.stock && line.stock.length > 0) {
                            initialWarehouses[line.id] = line.stock[0].entrepot_id;
                        }
                    });
                }

                setPreparedQuantities(initialQuantities);
                setSelectedWarehouses(initialWarehouses);
            }
        } catch (error) {
            console.error('Error fetching expedition details:', error);
            addToast("Erreur", "Erreur lors du chargement des détails de l'expédition", "danger");
        } finally {
            setDetailsLoading(false);
        }
    };

    const saveExpedition = async (closeOrder: boolean = false) => {
        if (!expeditionData) return;
        setSaving(true);
        
        try {
            const lines = Object.entries(preparedQuantities).map(([lineId, quantity]) => ({
                id: parseInt(lineId),
                qty_shipped: quantity
            }));

            const data: any = {
                action: 'setexpedition',
                expeditionid: parseInt(expeditionData.id),
                lines: lines,
                entity: user.entity,
                closed: isFranchiseOrder ? (closeOrder ? 1 : 0) : 0
            };

            
            

            const response : any = await axios.post<ValidationResponse>(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data && response.success) {
                const expedition = expeditionData;
                expedition.orderinfo.validation = response.order_validation || "";
                setExpeditionData(expedition);
                setValidationResponse(response.data);

                // Vérifier si la quantité préparée est inférieure à la quantité commandée ET qu'il y a du stock disponible
                const isQuantityIssue = expedition.lines?.some(line => {
                    const preparedQty = preparedQuantities[line.id] || 0;
                    const orderedQty = parseInt(line.qty) || 0;
                    
                    // Récupérer le stock selon l'entrepôt sélectionné
                    const selectedWarehouse = selectedWarehouses[line.id];
                    let availableStock = 0;
                    
                    if (line.stock && Array.isArray(line.stock)) {
                        if (selectedWarehouse) {
                            // Chercher le stock pour l'entrepôt sélectionné
                            const selectedStock = line.stock.find(s => 
                                s.entrepot_id == selectedWarehouse || 
                                s.entrepot_id === selectedWarehouse
                            );
                            availableStock = selectedStock ? parseInt(selectedStock.stock) || 0 : 0;
                        } else {
                            // Si aucun entrepôt sélectionné, prendre le premier disponible
                            availableStock = line.stock.length > 0 ? parseInt(line.stock[0].stock) || 0 : 0;
                        }
                    }
                    
                    // Vérifier: préparé < commandé ET préparé < stock disponible
                    // ET s'assurer qu'il y a effectivement du stock disponible
                    return preparedQty < orderedQty && preparedQty < availableStock && availableStock > 0;
                });

                // Gestion des modals selon la présence de PDF et de problème de quantité
                if (response.resultPDF) {
                    setPdfUrl(response.resultPDF); 
                    setPdfFileName(`expedition_${expeditionData.ref}_${new Date().toISOString().split('T')[0]}.pdf`);
                    
                    // Si problème de quantité, afficher le modal de notification au lieu du modal PDF
                    if (isQuantityIssue) {
                        setShowQuantityIssueModal(true);
                        addToast("Succès", "Expédition validée avec succès !", "success");
                    } else {
                        // Si pas de problème de quantité, afficher le modal PDF normal
                        setPdfModalOpen(true);
                        addToast("Succès", "Expédition validée avec succès ! PDF généré.", "success");
                    }
                } else {
                    // Si pas de PDF généré
                    if (isQuantityIssue) {
                        setShowQuantityIssueModal(true);
                        addToast("Succès", "Expédition validée avec succès !", "success");
                    } else {
                        addToast("Succès", "Expédition validée avec succès !", "success");
                    }
                }
                
                // Afficher un message spécifique seulement pour les franchises
                if (isFranchiseOrder) {
                    if (data.closed === 1) {
                        addToast("Information", "La commande franchise a été clôturée", "info");
                    } else if (data.closed === 0) {
                        addToast("Information", "La commande franchise reste ouverte", "info");
                    }
                }

                const shouldClearExpedition = expeditionData.orderinfo.validation === "1" || !isAllLinesValidated();

                if (shouldClearExpedition) {
                    changeOrder(null);
                    setExpeditionData(null);
                    setExpeditionId(null);
                    setPreparedQuantities({});
                    setSelectedWarehouses({});
                }
            } else {
                addToast("Erreur", "Erreur lors de la validation de l'expédition", "danger");
            }
        } catch (error) {
            console.error('Error saving expedition:', error);
            addToast("Erreur", "Erreur lors de la validation de l'expédition", "danger");
        } finally {
            setSaving(false);
        }
    };

    const handleValidateExpedition = () => {
        if (!expeditionData) {
            addToast("Attention", "Aucune expédition sélectionnée", "warning");
            return;
        }
        
        const hasInvalidQuantities = expeditionData?.lines.some(line => {
            const preparedQty = preparedQuantities[line.id] || 0;
            const maxQty = parseInt(line.qty);
            return preparedQty < 0 || preparedQty > maxQty;
        });

        if (hasInvalidQuantities) {
            addToast("Erreur", "Certaines quantités sont invalides. Veuillez vérifier les quantités préparées.", "danger");
            return;
        }

        // Vérifier si toutes les lignes sont complètes
        const allLinesComplete = checkAllLinesComplete();
        
        // Afficher la modal de clôture UNIQUEMENT pour les franchises
        if (!allLinesComplete && isFranchiseOrder) {
            // Afficher la modal de confirmation pour la clôture des franchises
            setConfirmCloseModalOpen(true);
        } else {
            // Pour les comptoirs ou si toutes les lignes sont complètes, passer directement à la validation
            setConfirmModalOpen(true);
        }
    };

    const handleConfirmClose = (closeOrder: boolean) => {
        
        setConfirmCloseModalOpen(false);
        
        // Lancer directement la validation avec la décision de clôture
        const executeValidation = async () => {
            await saveExpedition(closeOrder);
        };
        
        executeValidation();
    };

    useEffect(() => {
        fetchCommandes();
        const intervalId = setInterval(fetchCommandes, 10000);
        return () => clearInterval(intervalId);
    }, []);

    // Update allOrders when activeTab changes
    useEffect(() => {
        const currentOrders = activeTab === 'comptoir' ? comptoirOrders : franchisesOrders;
        setAllOrders(currentOrders);
    }, [activeTab, comptoirOrders, franchisesOrders]);

    const handleCommandeSelect = async (selectedOrder: SearchResult) => {
        changeOrder(selectedOrder);
        const orderId = parseInt(selectedOrder.id);
        await fetchExpeditionDetails(orderId);
    };

    const handleQuantityChange = (lineId: string, value: string) => {
        const quantity = parseInt(value) || 0;
        const line = expeditionData?.lines.find(line => line.id === lineId);

        if (!line) return;

        const maxQty = parseInt(line.qty);
        const alreadyPrepared = parseInt(line.qty_shipped) || 0;
        const minQty = alreadyPrepared; // Cannot go below already prepared quantity

        // Validate the quantity
        let newQuantity = quantity;

        if (quantity < minQty) {
            addToast("Attention", `Impossible de diminuer la quantité en dessous de ${minQty} (déjà préparée)`, "warning");
            newQuantity = minQty;
        } else if (quantity > maxQty) {
            addToast("Attention", `Quantité ne peut pas dépasser ${maxQty} (quantité commandée)`, "warning");
            newQuantity = maxQty;
        }

        setPreparedQuantities(prev => ({
            ...prev,
            [lineId]: newQuantity
        }));
    };

    const handleWarehouseChange = (lineId: string, warehouseId: string) => {
        setSelectedWarehouses(prev => ({
            ...prev,
            [lineId]: warehouseId
        }));
    };

    const totalPrepared = Object.values(preparedQuantities).reduce((sum, qty) => sum + qty, 0);
    const totalOrdered = expeditionData?.lines.reduce((sum, line) => sum + parseInt(line.qty), 0) || 0;
    const isExpeditionComplete = expeditionData?.lines.every(line => parseInt(line.qty_shipped || '0') >= parseInt(line.qty)) || false;

    const isAllLinesValidated = () => {
        if (!expeditionData) return false;

        return expeditionData.lines.some(line => {
            const selectedWarehouse = selectedWarehouses[line.id] ||
                (line.stock.length > 0 ? line.stock[0].entrepot_id : '');
            const selectedStock = line.stock.find(s => s.entrepot_id === selectedWarehouse);
            const preparedQty = preparedQuantities[line.id] || 0;
            const orderedQty = parseInt(line.qty);
            const stockQty = selectedStock ? parseInt(selectedStock.stock) : 0;
            return stockQty > 0 && preparedQty != orderedQty;
        });
    };

    return (
        <React.Fragment>
            <style>
                {`
                    @keyframes blink {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                    }
                    body {
                        font-family: 'Inter', sans-serif;
                        background-color: #F9FAFB;
                    }
                    .modern-card {
                        background-color: #FFFFFF;
                        border-radius: 8px;
                        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                        border: 1px solid #E5E7EB;
                    }
                    .modern-card-header {
                        background-color: #2563EB;
                        color: #FFFFFF;
                        border-radius: 8px 8px 0 0;
                        padding: 12px 16px;
                        border-bottom: none;
                    }
                    .modern-table {
                        border-collapse: separate;
                        border-spacing: 0;
                        font-size: 12px !important;
                        width: 100%;
                    }
                    .modern-table th {
                        background-color: #F3F4F6;
                        color: #1F2937;
                        font-weight: 600;
                        padding: 8px 10px;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        white-space: nowrap;
                    }
                    .modern-table td {
                        padding: 8px 10px;
                        font-size: 12px;
                        color: #1F2937;
                        border-bottom: 1px solid #E5E7EB;
                        vertical-align: middle;
                    }
                    .modern-table tr:nth-child(odd) {
                        background-color: #F9FAFB;
                    }
                    .modern-table tr:hover {
                        background-color: #E0E7FF;
                    }
                    .commande-list-item {
                        transition: background-color 0.2s ease;
                        border-radius: 6px;
                    }
                    .commande-list-item:hover {
                        background-color: #F3F4F6;
                    }
                    .commande-list-item.active {
                        background-color: #d6d6d650;
                        border-color: #2563EB;
                    }
                    .modern-input {
                        border: 1px solid #D1D5DB;
                        border-radius: 6px;
                        padding: 4px 8px;
                        font-size: 12px;
                        transition: border-color 0.2s ease;
                        height: 32px;
                    }
                    .modern-input:focus {
                        border-color: #2563EB;
                        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                        outline: none;
                    }
                    .modern-input:disabled {
                        background-color: #F3F4F6;
                        color: #6B7280;
                        cursor: not-allowed;
                    }
                    .modern-badge {
                        background-color: #6B7280;
                        color: #FFFFFF;
                        font-size: 10px;
                        padding: 2px 6px;
                        border-radius: 12px;
                    }
                    .no-commande-card {
                        background-color: #FFFFFF;
                        border-radius: 8px;
                        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                        border: 1px solid #E5E7EB;
                        text-align: center;
                        padding: 24px;
                    }
                    .commande-list-container {
                        background-color: #FFFFFF;
                        border-radius: 8px;
                        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                        border: 1px solid #E5E7EB;
                        width: 100%;
                    }

                    .validate-btn {
                        background-color: #10B981;
                        border-color: #10B981;
                        color: white;
                        font-weight: 600;
                        padding: 8px 20px;
                        border-radius: 6px;
                        transition: all 0.2s ease;
                        font-size: 13px;
                    }
                    .validate-btn:hover {
                        background-color: #059669;
                        border-color: #059669;
                        transform: translateY(-1px);
                        box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
                    }
                    .validate-btn:disabled {
                        background-color: #6B7280;
                        border-color: #6B7280;
                        cursor: not-allowed;
                    }
                    .dropdown-toggle-custom {
                        background-color: white !important;
                        color: #1F2937 !important;
                        border: 1px solid #D1D5DB !important;
                        min-width: 120px;
                        font-size: 12px;
                        padding: 4px 10px;
                        height: 32px;
                    }
                    .dropdown-toggle-custom:focus {
                        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
                    }
                    .commande-title {
                        font-size: 12px;
                        color: white;
                        margin: 0;
                    }
                    .action-btn {
                        padding: 3px 8px;
                        font-size: 11px;
                        border-radius: 4px;
                        min-width: 70px;
                        height: 28px;
                    }
                    .commander-btn {
                        background-color: #3B82F6;
                        border-color: #3B82F6;
                    }
                    .commander-btn:hover {
                        background-color: #2563EB;
                        border-color: #2563EB;
                    }
                    .commander-btn:disabled {
                        background-color: #9CA3AF;
                        border-color: #9CA3AF;
                        cursor: not-allowed;
                    }
                    .voir-btn {
                        background-color: #10B981;
                        border-color: #10B981;
                    }
                    .voir-btn:hover {
                        background-color: #059669;
                        border-color: #059669;
                    }
                    .supplier-list-item {
                        cursor: pointer;
                        transition: all 0.2s ease;
                        padding: 6px 10px !important;
                        font-size: 12px;
                    }
                    .supplier-list-item:hover {
                        background-color: #f8f9fa;
                    }
                    .supplier-list-item.selected {
                        background-color: #E0E7FF;
                        border-color: #3B82F6;
                    }
                    .success-modal .modal-header {
                        background-color: #10B981 !important;
                        color: white !important;
                        border-bottom: none;
                    }
                    .success-modal .modal-content {
                        border: 2px solid #10B981;
                        border-radius: 12px;
                    }
                    .success-modal .btn-success {
                        background-color: #10B981;
                        border-color: #10B981;
                    }
                    .success-modal .btn-success:hover {
                        background-color: #059669;
                        border-color: #059669;
                    }
                    .toast-container {
                        position: fixed;
                        top: 80px;
                        right: 20px;
                        z-index: 9999;
                        min-width: 300px;
                    }
                    .toast-success {
                        border-left: 4px solid #10B981;
                    }
                    .toast-danger {
                        border-left: 4px solid #EF4444;
                    }
                    .toast-warning {
                        border-left: 4px solid #F59E0B;
                    }
                    .toast-info {
                        border-left: 4px solid #3B82F6;
                    }
                    .quantity-info {
                        font-size: 10px;
                        color: #6B7280;
                        display: block;
                        margin-top: 2px;
                    }
                    .dropdown-menu-custom {
                        max-height: 200px;
                        overflow-y: auto;
                        font-size: 12px;
                        min-width: 180px;
                    }
                    .dropdown-item-custom {
                        padding: 4px 8px !important;
                        font-size: 12px;
                    }
                    .table-responsive {
                        max-width: 100%;
                        overflow-x: auto;
                    }
                    .dropdown-toggle-sm {
                        padding: 2px 8px !important;
                        font-size: 11px !important;
                        height: 26px !important;
                    }
                    .tab-header {
                        display: flex;
                        justify-content: center;
                        margin-bottom: 12px;
                        background: white;
                        border-radius: 8px;
                        padding: 4px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    }
                    .tab-nav-link {
                        padding: 8px 20px !important;
                        font-size: 13px !important;
                        font-weight: 600;
                        color: #6B7280;
                        border-radius: 6px;
                        transition: all 0.2s ease;
                        border: none !important;
                    }
                    .tab-nav-link.active {
                        background-color: #657eb5 !important;
                        color: white !important;
                    }
                    .tab-nav-link:hover {
                        background-color: #f3f4f6;
                        color: #374151;
                    }
                    .orders-scroll-container {
                        max-height: calc(100vh - 300px);
                        overflow-y: auto;
                        padding-right: 4px;
                    }
                    .orders-scroll-container::-webkit-scrollbar {
                        width: 6px;
                    }
                    .orders-scroll-container::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 10px;
                    }
                    .orders-scroll-container::-webkit-scrollbar-thumb {
                        background: #c1c1c1;
                        border-radius: 10px;
                    }
                    .orders-scroll-container::-webkit-scrollbar-thumb:hover {
                        background: #a1a1a1;
                    }
                    .quantity-issue-modal .modal-header {
                        background-color: #F59E0B !important;
                        color: white !important;
                        border-bottom: none;
                    }
                    .quantity-issue-modal .modal-content {
                        border: 2px solid #F59E0B;
                        border-radius: 12px;
                    }
                `}
            </style>
            {/* Toast Notifications Container */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast key={toast.id} className={`mb-2 ${toast.color === 'success' ? 'toast-success' : ''} ${toast.color === 'danger' ? 'toast-danger' : ''} ${toast.color === 'warning' ? 'toast-warning' : ''} ${toast.color === 'info' ? 'toast-info' : ''}`}>
                        <ToastHeader toggle={() => removeToast(toast.id)} className={`bg-${toast.color} text-white`}>
                            {toast.title}
                        </ToastHeader>
                        <ToastBody>
                            {toast.message}
                        </ToastBody>
                    </Toast>
                ))}
            </div>

            {/* Modal pour les problèmes de quantité */}
            <Modal isOpen={showQuantityIssueModal} toggle={() => setShowQuantityIssueModal(false)} centered className="quantity-issue-modal" size="md">
                <ModalHeader toggle={() => setShowQuantityIssueModal(false)} className="bg-warning text-white">
                    <i className="ri-alert-line me-2"></i>
                    Notification des Quantités Manquantes
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className="ri-information-line display-1 text-warning"></i>
                    </div>
                    <h5 className="fs-16 mb-3">Le vendeur sera notifié</h5>
                    
                    <div className="alert alert-info mt-3">
                        <i className="ri-lightbulb-line me-2"></i>
                        <small>
                            Cette notification permet au vendeur de prendre les mesures nécessaires 
    
                        </small>
                    </div>
                    
                </ModalBody>
                <ModalFooter className="justify-content-center">
                    <Button 
                        color="warning" 
                        onClick={() => setShowQuantityIssueModal(false)}
                    >
                        <i className="ri-check-line me-1"></i>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal de confirmation de clôture pour FRANCHISES uniquement */}
            <Modal isOpen={confirmCloseModalOpen} toggle={() => setConfirmCloseModalOpen(false)} centered>
                <ModalHeader toggle={() => setConfirmCloseModalOpen(false)} className="fs-16">
                    <i className="ri-alert-line me-2 text-warning"></i>
                    Clôture commande FRANCHISE
                </ModalHeader>
                <ModalBody className="py-4">
                    <div className="text-center mb-3">
                        <i className="ri-store-line display-4 text-warning"></i>
                        <h5 className="mt-3">Commande FRANCHISE - Quantités incomplètes</h5>
                    </div>
                    <div className="alert alert-warning">
                        <i className="ri-information-line me-2"></i>
                        <strong>Attention :</strong> Cette commande est une commande FRANCHISE et contient des lignes non complètement préparées.
                    </div>
                    <div className="alert alert-info">
                        <i className="ri-question-line me-2"></i>
                        <strong>Que souhaitez-vous faire ?</strong>
                        <div className="mt-2 small">
                            • <strong>Oui, clôturer</strong> : La commande FRANCHISE sera clôturée (statut = Livrée)<br/>
                            • <strong>Non, laisser ouverte</strong> : La commande FRANCHISE restera ouverte pour les quantités manquantes
                        </div>
                    </div>
                    <div className="alert alert-light border">
                        <i className="ri-information-line me-2"></i>
                        <small><strong>Note :</strong> Cette option n'est disponible que pour les commandes FRANCHISES</small>
                    </div>
                </ModalBody>
                <ModalFooter className="text-center" style={{ textAlign: 'center' }}>
                    <Button
                        color="warning"
                        onClick={() => handleConfirmClose(false)}
                        className="fs-14 me-2"
                    >
                        <i className="ri-close-line me-1"></i>
                        Non, laisser ouverte
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => handleConfirmClose(true)}
                        className="fs-14"
                    >
                        <i className="ri-check-line me-1"></i>
                        Oui, clôturer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal PDF */}
            <Modal isOpen={pdfModalOpen} toggle={() => setPdfModalOpen(false)} centered className="pdf-modal" size="md">
                <ModalHeader toggle={() => setPdfModalOpen(false)} className="bg-success text-white">
                    <i className="ri-checkbox-circle-line me-2"></i>
                    Expédition Validée avec Succès
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className="ri-check-double-line display-1 text-success"></i>
                    </div>
                    <p className="fs-16 mb-3">Expédition validée avec succès ! PDF généré !</p>
                    {validationResponse && isFranchiseOrder && (
                        <div className="alert alert-info mt-3">
                            <i className="ri-information-line me-2"></i>
                            {validationResponse.closed === 1 
                                ? "La commande FRANCHISE a été clôturée " 
                                : "La commande FRANCHISE reste ouverte "}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="justify-content-center">
                    {pdfUrl && (
                        <>
                            <Button
                                color="primary"
                                onClick={() => window.open(pdfUrl, '_blank')}
                                className="me-2"
                            >
                                <i className="ri-external-link-line me-1"></i>
                                Voir PDF
                            </Button>
                            <Button
                                color="success"
                                onClick={() => {
                                    // Create a temporary link to download the PDF
                                    const link = document.createElement('a');
                                    link.href = pdfUrl;
                                    link.download = pdfFileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                className="me-2"
                            >
                                <i className="ri-download-2-line me-1"></i>
                                Télécharger PDF
                            </Button>
                        </>
                    )}
                    <Button color="secondary" onClick={() => setPdfModalOpen(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Confirmation Modal standard (pour comptoirs ou commandes complètes) */}
            <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)} centered>
                <ModalHeader toggle={() => setConfirmModalOpen(false)} className="fs-16">
                    <i className="ri-alert-line me-2 text-warning"></i>
                    Confirmation
                </ModalHeader>
                <ModalBody className="py-4">
                    <div className="text-center mb-3">
                        <i className="ri-question-line display-4 text-warning"></i>
                        <h5 className="mt-3">Êtes-vous sûr de vouloir valider cette expédition ?</h5>
                    </div>
                    <div className="alert alert-info">
                        <i className="ri-information-line me-2"></i>
                        Vérifiez que toutes les quantités préparées sont correctes.
                    </div>
                </ModalBody>
                <ModalFooter className="text-center" style={{ textAlign: 'center' }}>
                    <Button
                        color="secondary"
                        onClick={() => setConfirmModalOpen(false)}
                        className="fs-14"
                    >
                        <i className="ri-close-line me-1"></i>
                        Annuler
                    </Button>
                    <Button
                        color="primary"
                        onClick={async () => {
                            setConfirmModalOpen(false);
                            // Pour les comptoirs ou commandes complètes, ne pas clôturer
                            await saveExpedition(false);
                        }}
                        className="fs-14"
                    >
                        <i className="ri-check-line me-1"></i>
                        Valider l'expédition
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Success Modal */}
            <Modal isOpen={successModalOpen} toggle={() => setSuccessModalOpen(false)} centered>
                <ModalHeader toggle={() => setSuccessModalOpen(false)} className="">
                    <i className="ri-checkbox-circle-line me-2"></i>
                    Commande créée avec succès
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className="ri-checkbox-circle-fill text-success display-3"></i>
                    </div>
                    <h5 className="text-success mb-3">Commande fournisseur créée !</h5>
                    <p className="mb-0">
                        Référence de la commande : <strong>{createdOrderRef}</strong>
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="success"
                        onClick={() => {
                            setSuccessModalOpen(false);
                            if (order) {
                                fetchExpeditionDetails(parseInt(order.id));
                            }
                        }}
                    >
                        <i className="ri-check-line me-1"></i> OK
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Commander/Voir Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>{modalTitle}</ModalHeader>
                <ModalBody>
                    {modalLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <div className="mt-2">Chargement en cours...</div>
                        </div>
                    ) : (
                        <>
                            <p className="mb-3">{modalMessage}</p>
                            {selectedLine && modalType === "commander" && (
                                <div className="mt-3">
                                    {availableSuppliers.length > 0 ? (
                                        <>
                                            <h6 className="mb-3">Sélectionnez un fournisseur:</h6>
                                            <ListGroup>
                                                {availableSuppliers.map((supplier, index) => (
                                                    <ListGroupItem
                                                        key={index}
                                                        tag="button"
                                                        action
                                                        className={`supplier-list-item ${selectedSupplier === supplier.idwh ? 'selected' : ''}`}
                                                        onClick={() => setSelectedSupplier(supplier.idwh)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{supplier.idwh}</strong>
                                                                <div className="text-muted small">
                                                                    {supplier.supplier_name || supplier.warehouse_name}
                                                                </div>
                                                                <strong>{supplier.price.toFixed(2)} €</strong>
                                                            </div>
                                                            {selectedSupplier === supplier.idwh && (
                                                                <i className="ri-check-line text-primary"></i>
                                                            )}
                                                        </div>
                                                    </ListGroupItem>
                                                ))}
                                            </ListGroup>
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="ri-error-warning-line display-5 text-muted"></i>
                                            <p className="text-muted mt-2">Aucun fournisseur disponible pour ce produit.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            {selectedLine && modalType === "voir" && selectedLine.supplierOrderData && (
                                <div className="mt-4">
                                    <Card className="modern-card">
                                        <CardBody>
                                            {selectedLine.supplierOrderData ? (
                                                <>
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Référence:</small>
                                                                <div className="fw-bold">{selectedLine.supplierOrderData.ref || "N/A"}</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Statut:</small>
                                                                <Badge
                                                                    color={
                                                                        selectedLine.supplierOrderData.statut === "0" ? "warning" :
                                                                                selectedLine.supplierOrderData.statut === "1" || selectedLine.supplierOrderData.statut === "2" ? "info" :
                                                                                selectedLine.supplierOrderData.statut === "4" ? "primary" :
                                                                                selectedLine.supplierOrderData.statut === "5" ? "success" :
                                                                                    selectedLine.supplierOrderData.statut === "6" ? "danger" : "secondary"
                                                                    }
                                                                    className="ms-2"
                                                                >
                                                                        {
                                                                            selectedLine.supplierOrderData.statut === "0" ? "Brouillon" :
                                                                                selectedLine.supplierOrderData.statut === "1" || selectedLine.supplierOrderData.statut === "2" ? "Commandé" :
                                                                                    selectedLine.supplierOrderData.statut === "4" ? "Partiellement reçu" :
                                                                                        selectedLine.supplierOrderData.statut === "5" ? "Reçu" :
                                                                                            selectedLine.supplierOrderData.statut === "6" || selectedLine.supplierOrderData.statut === "7" ? "Annulé" :
                                                                                                "Inconnu"
                                                                        }
                                                                </Badge>
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Date:</small>
                                                                <div className="fw-bold">
                                                                    {selectedLine.supplierOrderData.date && !isNaN(Number(selectedLine.supplierOrderData.date)) ?
                                                                        new Date(Number(selectedLine.supplierOrderData.date) * 1000).toLocaleDateString() :
                                                                        "N/A"}
                                                                </div>
                                                                <div className="">
                                                                    {selectedLine.supplierOrderData?.thirdparty && (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <small className="text-muted">Nom du fournisseur:</small>
                                                                                <div className="fw-bold">{selectedLine.supplierOrderData.thirdparty.name || "N/A"}</div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <div className="table-responsive mt-4">
                                                        <table className="table table-sm modern-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Référence</th>
                                                                    <th>Description</th>
                                                                    <th className="text-center">Quantité</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {selectedLine.supplierOrderData.lines &&
                                                                    selectedLine.supplierOrderData.lines.length > 0 ? (
                                                                    selectedLine.supplierOrderData.lines.map((lineItem: SupplierOrderLine) => (
                                                                        <tr key={lineItem.id}>
                                                                            <td className="fw-semibold">{lineItem.product_ref}</td>
                                                                            <td>{lineItem.product_label || lineItem.desc}</td>
                                                                            <td className="text-center">{lineItem.qty}</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={5} className="text-center">
                                                                            Aucune ligne de commande
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-error-warning-line display-5 text-muted"></i>
                                                    <p className="text-muted mt-2">Aucune donnée de commande disponible.</p>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        {modalType === "commander" ? "Annuler" : "Fermer"}
                    </Button>
                    {modalType === "commander" && (
                        <Button
                            color="primary"
                            onClick={handleCreateSupplierOrder}
                            disabled={!selectedSupplier || modalLoading}
                        >
                            {modalLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Création...
                                </>
                            ) : (
                                "Créer la commande"
                            )}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            <div className="page-content pb-0">
                <Container fluid>
                    <Row>
                        <Col xl="12" lg="12" style={{ paddingRight: '8px' }}>
                            <Row>
                                <Col xl="9" lg="8" style={{ paddingRight: '4px' }}>
                                    {order ? (
                                        <Card className="mb-4 modern-card">
                                            <CardHeader className="modern-card-header" style={{
                                                backgroundColor: "#657eb5",
                                                padding: "8px 12px"
                                            }}>
                                                <Row className="align-items-center">
                                                    <Col md={3}>
                                                        {expeditionId && (
                                                            <Badge color="success" style={{ fontSize: '10px' }}>
                                                                Expedition: {expeditionId}
                                                            </Badge>
                                                        )}
                                                    </Col>
                                                    <Col md={6} className="text-center">
                                                        {expeditionData && order.ref && (
                                                            <h5 className="commande-title mb-0">
                                                                Commande : {order.ref}
                                                                {isFranchiseOrder 
                                                                    // <Badge color="warning" className="ms-2" style={{ fontSize: '9px' }}>
                                                                    //     FRANCHISE
                                                                    // </Badge>
                                                                }
                                                            </h5>
                                                        )}
                                                    </Col>
                                                    <Col md={3} className="text-end">
                                                        <span style={{ fontSize: '10px', color: '#F3F4F6' }}>
                                                            Date: {new Date(order.date).toLocaleDateString()}
                                                        </span>
                                                    </Col>
                                                </Row>
                                                {expeditionData && (
                                                    <Row className="mt-2">
                                                        <Col md={6}>
                                                            <small style={{ color: '#F3F4F6', fontSize: '10px' }}>
                                                                Client: {expeditionData.orderinfo.client_name}
                                                            </small>
                                                        </Col>
                                                        <Col md={6} className="text-end">
                                                            <small style={{ color: '#F3F4F6', fontSize: '10px' }}>
                                                                Créé par: {expeditionData.orderinfo.created_by}
                                                            </small>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </CardHeader>
                                            <CardBody style={{ padding: '12px' }}>
                                                {detailsLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner color="primary" />
                                                        <div className="mt-2" style={{ fontSize: '12px' }}>Chargement des détails...</div>
                                                    </div>
                                                ) : expeditionData ? (
                                                    <>
                                                        <div className="table-responsive" style={{ maxWidth: '100%' }}>
                                                            <table className="table table-sm modern-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: '8%', fontSize: '11px' }}>Référence</th>
                                                                        <th style={{ width: '30%', fontSize: '11px' }}>Produit</th>
                                                                        <th className="text-center" style={{ width: '8%', fontSize: '11px' }}>Qt Commandée</th>
                                                                        <th className="text-center" style={{ width: '10%', fontSize: '11px' }}>Qt Préparée</th>
                                                                        <th className="text-center" style={{ width: '8%', fontSize: '11px' }}>En Stock</th>
                                                                        <th className="text-center" style={{ width: '15%', fontSize: '11px' }}>Entrepôt</th>
                                                                        <th className="text-center" style={{ width: '11%', fontSize: '11px' }}>Cmd fourn.</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {expeditionData.lines.map((line: ExpeditionLine) => {
                                                                        const selectedWarehouse = selectedWarehouses[line.id] ||
                                                                            (line.stock.length > 0 ? line.stock[0].entrepot_id : '');
                                                                        const selectedStock = line.stock.find(s => s.entrepot_id === selectedWarehouse);
                                                                        const orderedQty = parseInt(line.qty);
                                                                        const preparedQty = preparedQuantities[line.id] || 0;
                                                                        const alreadyPrepared = parseInt(line.qty_shipped) || 0;
                                                                        const isFullyPrepared = preparedQty === orderedQty;
                                                                        const isLineAlreadyFullyPrepared = alreadyPrepared >= orderedQty;
                                                                        const minQty = alreadyPrepared; // Minimum quantity is what's already prepared

                                                                        return (
                                                                            <tr key={line.id} style={{ fontSize: '12px' }}>
                                                                                <td className="fw-semibold" style={{ fontSize: '11px' }}>{line.ref}</td>
                                                                                <td style={{ maxWidth: '250px' }}>
                                                                                    <div className="text-truncate-custom" title={line.desc}>
                                                                                        {line.libelle ?? line.desc}
                                                                                    </div>
                                                                                    {line.comment && (
                                                                                        <div
                                                                                            className="text-muted"
                                                                                            title={line.comment}
                                                                                            style={{ fontSize: '11px', marginTop: '2px' }}
                                                                                        >
                                                                                            <i className="ri-chat-1-line me-1" style={{ fontSize: '10px' }}></i>
                                                                                            {line.comment.length > 30 ? line.comment.substring(0, 30) + '...' : line.comment}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-center" style={{ fontSize: '11px' }}>{line.qty}</td>

                                                                                <td className="text-center">
                                                                                    <div>
                                                                                        <Input
                                                                                            type="number"
                                                                                            min={minQty}
                                                                                            max={parseInt(line.qty)}
                                                                                            value={preparedQuantities[line.id] || 0}
                                                                                            onChange={(e) => handleQuantityChange(line.id, e.target.value)}
                                                                                            className="modern-input text-center"
                                                                                            style={{ width: '60px', margin: '0 auto', fontSize: '11px' }}
                                                                                            disabled={isLineAlreadyFullyPrepared}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <Badge
                                                                                        color={selectedStock && parseInt(selectedStock.stock) > 0 ? "success" : "danger"}
                                                                                        style={{ fontSize: '11px', padding: '3px 6px' }}
                                                                                    >
                                                                                        {selectedStock ? selectedStock.stock : '0'}
                                                                                    </Badge>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {line.stock.length > 0 ? (
                                                                                        <Dropdown
                                                                                            isOpen={dropdownOpen[line.id] || false}
                                                                                            toggle={() => toggleDropdown(line.id)}
                                                                                            direction="down"
                                                                                        >
                                                                                            <DropdownToggle
                                                                                                caret
                                                                                                className="dropdown-toggle-custom dropdown-toggle-sm"
                                                                                                style={{ minWidth: '100px' }}
                                                                                            >
                                                                                                <span className="text-truncate-custom" style={{ display: 'inline-block', maxWidth: '80px' }}>
                                                                                                    {selectedStock ? selectedStock.entrepot_name : 'Sélectionner'}
                                                                                                </span>
                                                                                            </DropdownToggle>
                                                                                            <DropdownMenu className="dropdown-menu-custom">
                                                                                                {line.stock.map((stockItem: Stock) => (
                                                                                                    <DropdownItem
                                                                                                        key={stockItem.entrepot_id}
                                                                                                        onClick={() => handleWarehouseChange(line.id, stockItem.entrepot_id)}
                                                                                                        active={selectedWarehouse === stockItem.entrepot_id}
                                                                                                        className="dropdown-item-custom"
                                                                                                    >
                                                                                                        <div className="d-flex justify-content-between align-items-center w-100">
                                                                                                            <span className="text-truncate-custom" style={{ maxWidth: '100px' }}>
                                                                                                                {stockItem.entrepot_name}
                                                                                                            </span>
                                                                                                            <Badge
                                                                                                                color={parseInt(stockItem.stock) > 0 ? "success" : "danger"}
                                                                                                                className="ms-2"
                                                                                                                style={{ fontSize: '9px', minWidth: '30px', textAlign: 'center' }}
                                                                                                            >
                                                                                                                {stockItem.stock}
                                                                                                            </Badge>
                                                                                                        </div>
                                                                                                    </DropdownItem>
                                                                                                ))}
                                                                                            </DropdownMenu>
                                                                                        </Dropdown>
                                                                                    ) : (
                                                                                        <Badge color="warning" style={{ fontSize: '10px' }}>Pas de stock</Badge>
                                                                                    )}
                                                                                </td>

                                                                                <td className="text-center">
                                                                                    {!line.commandefourndet ||
                                                                                        line.commandefourndet === "0" ||
                                                                                        line.commandefourndet === "null" ||
                                                                                        line.commandefourndet === "" ? (
                                                                                        <Button
                                                                                            color="primary"
                                                                                            size="sm"
                                                                                            className="action-btn commander-btn"
                                                                                            onClick={() => openCommanderModal(line)}
                                                                                            disabled
                                                                                        >
                                                                                            <i className="ri-shopping-cart-line me-1"></i>
                                                                                            Commander
                                                                                        </Button>
                                                                                    ) : (
                                                                                        <Button
                                                                                            color="success"
                                                                                            size="sm"
                                                                                            className="action-btn voir-btn"
                                                                                            onClick={() => openVoirModal(line)}
                                                                                        >
                                                                                            <i className="ri-eye-line me-1"></i>
                                                                                            Voir
                                                                                        </Button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="mt-3" style={{ fontSize: '12px' }}>
                                                            <Row className="align-items-center">
                                                                <Col md={6}>
                                                                    <div className="d-flex gap-4">
                                                                        <div>
                                                                            <small className="text-muted">Total commandé:</small>
                                                                            <div className="fw-bold">{totalOrdered}</div>
                                                                        </div>
                                                                        <div>
                                                                            <small className="text-muted">Total préparé:</small>
                                                                            <div className="fw-bold">{totalPrepared}</div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                {/* Hide both buttons if validation is "1" */}
                                                                    {expeditionData.status === "0" && expeditionData.orderinfo.validation !== "1" && (
                                                                    <Col md={6} className="text-end">
                                                                        <Button
                                                                            className="validate-btn"
                                                                            id="btnvalider"
                                                                            onClick={handleValidateExpedition}
                                                                            disabled={saving || isExpeditionComplete && expeditionData.status != "0" || (activeTab === "comptoir" && expeditionData.orderinfo.validation === "1")}
                                                                            style={{
                                                                                padding: '6px 16px',
                                                                                fontSize: '12px',
                                                                                backgroundColor: (activeTab === "franchises") || (expeditionData.orderinfo.validation === "2" || !isAllLinesValidated()) ? '#10B981' : '#F59E0B',
                                                                                borderColor: (activeTab === "franchises") || (expeditionData.orderinfo.validation === "2" || !isAllLinesValidated()) ? '#10B981' : '#F59E0B'
                                                                            }}
                                                                        >
                                                                            {saving ? (
                                                                                <>
                                                                                    <Spinner size="sm" className="me-2" />
                                                                                    En cours...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="ri-check-double-line me-2"></i>
                                                                                    {(activeTab === "franchises") || (expeditionData.orderinfo.validation === "2" || !isAllLinesValidated()) ? 'Valider l\'Expédition' : 'valider mon choix'}
                                                                                </>
                                                                            )}
                                                                        </Button>
                                                                    </Col>
                                                                    )}
                                                                    {/* Notification si en attente vendeur :  status "1" */}
                                                                    {expeditionData.orderinfo.validation === "1" && (
                                                                        <Col md={6} className="text-center">
                                                                            <Alert color="danger" className='material-shadow mb-0'>
                                                                                La commande est en <strong>attente de confirmation</strong> du vendeur <strong>{expeditionData.orderinfo.created_by}</strong>!
                                                                            </Alert>

                                                                        </Col>
                                                                    )}
                                                            </Row>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <i className="ri-error-warning-line display-5 text-muted"></i>
                                                        <p className="text-muted mt-2" style={{ fontSize: '12px' }}>Aucune commande sélectionnée.</p>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <Card className="no-commande-card">
                                            <CardBody>
                                                <h5 style={{ fontSize: '16px', color: '#6B7280', marginBottom: '8px' }}>
                                                    Aucune commande sélectionnée
                                                </h5>
                                                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: 0 }}>
                                                    Veuillez sélectionner une commande pour afficher les détails.
                                                </p>
                                            </CardBody>
                                        </Card>
                                    )}
                                </Col>
                                <Col xl="3" lg="4" style={{ paddingLeft: '4px' }}>
                                    <div className="position-sticky" style={{ top: "80px" }}>
                                        <Card className="commande-list-container">
                                            <CardHeader style={{
                                                backgroundColor: '#657eb5',
                                                borderRadius: '8px 8px 0 0',
                                                padding: '6px 10px',
                                                textAlign: 'center'
                                            }}>
                                                <h6 style={{
                                                    fontSize: '12px',
                                                    color: 'white',
                                                    fontWeight: 400,
                                                    margin: 0
                                                }}>
                                                    Liste des Commandes
                                                </h6>
                                            </CardHeader>
                                            <CardBody style={{ padding: '12px' }}>
                                                {/* Tab Navigation */}
                                                <div className="tab-header mb-3">
                                                    <Nav pills className="w-100 d-flex">
                                                        <NavItem className="flex-grow-1 text-center">
                                                            <NavLink
                                                                className={`tab-nav-link ${activeTab === 'comptoir' ? 'active' : ''}`}
                                                                onClick={() => {setActiveTab('comptoir'); setIsFranchiseOrder(false);}}
                                                            >
                                                                Comptoir ({comptoirOrders.length})
                                                            </NavLink>
                                                        </NavItem>
                                                        <NavItem className="flex-grow-1 text-center">
                                                            <NavLink
                                                                className={`tab-nav-link ${activeTab === 'franchises' ? 'active' : ''}`}
                                                                onClick={() => {setActiveTab('franchises'); setIsFranchiseOrder(true);}}

                                                            >
                                                                Franchises ({franchisesOrders.length})
                                                            </NavLink>
                                                        </NavItem>
                                                    </Nav>
                                                </div>

                                                {/* Orders List */}
                                                <div className="orders-scroll-container">
                                                    {allOrders.length > 0 ? (
                                                        <ul className="list-group" style={{ listStyle: 'none', padding: 0 }}>
                                                            {allOrders.map((commande) => {
                                                                const hasExpeditions = commande.has_expeditions === true;
                                                                const hasDeliveryDate = commande.date_livraison && commande.date_livraison !== '0000-00-00';
                                                                const shouldHighlight = hasExpeditions && hasDeliveryDate;
                                                                const isFranchise = franchisesOrders.some(fOrder => fOrder.id === commande.id);
                                                                const isWaitingConfirmation = commande.validation == 1 && !hasDeliveryDate && !isFranchise;
                                                                
                                                                return (
                                                                    <li
                                                                        key={commande.id}
                                                                        className={`commande-list-item ${order?.id === commande.id ? 'active' : ''}`}
                                                                        onClick={() => handleCommandeSelect(commande)}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            padding: '8px 10px',
                                                                            marginBottom: '6px',
                                                                            border: '1px solid #E5E7EB',
                                                                            borderRadius: '6px',
                                                                            position: 'relative',
                                                                            overflow: 'visible',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    >
                                                                        {shouldHighlight && (
                                                               
                                                                            <Badge style={{
                                                                                position: 'absolute',
                                                                                bottom: '6px',
                                                                                right: '6px',
                                                                                animation: 'blink 1.5s infinite',
                                                                                zIndex: 1,
                                                                            }}  color="danger"> Confirmée </Badge>
                                                                        )}
                                                                        {isWaitingConfirmation && (

                                                                            <Badge style={{
                                                                                position: 'absolute',
                                                                                bottom: '6px',
                                                                                right: '6px',
                                                                                zIndex: 1,
                                                                            }} color="warning"> Attente confirmation </Badge>
                                                                        )}
                                                                        
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <span style={{
                                                                                fontSize: '12px',
                                                                                fontWeight: 600,
                                                                                color: '#1F2937'
                                                                            }}>
                                                                                {commande.ref}
                                                                            </span>
                                                                            <div>
                                                                                {isFranchise && (
                                                                                    <Badge className="modern-badge badge bg-warning me-1" style={{ fontSize: '8px', padding: '1px 3px' }}>
                                                                                        FR
                                                                                    </Badge>
                                                                                )}
                                                                                <Badge className="modern-badge badge bg-info" style={{ fontSize: '9px', padding: '1px 4px' }}>
                                                                                    {commande.author_fullname?.substring(0, 10)}
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                        <span style={{
                                                                            fontSize: '11px',
                                                                            fontWeight: 600,
                                                                            color: 'forestgreen'
                                                                        }}>
                                                                            {commande.client?.length > 20 ? commande.client.substring(0, 20) + '...' : commande.client}
                                                                        </span>
                                                                        <small style={{
                                                                            fontSize: '10px',
                                                                            color: '#6B7280',
                                                                            display: 'block',
                                                                            marginTop: '2px'
                                                                        }}>
                                                                            {new Date(commande.date).toLocaleDateString()}
                                                                        </small>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <i className="ri-error-warning-line text-muted mb-2" style={{ fontSize: '24px' }}></i>
                                                            <p style={{
                                                                fontSize: '11px',
                                                                color: '#6B7280',
                                                                textAlign: 'center',
                                                                padding: '12px 0',
                                                                margin: 0
                                                            }}>
                                                                {activeTab === 'comptoir'
                                                                    ? 'Aucune commande de comptoir à préparer.'
                                                                    : 'Aucune commande de franchise à préparer.'}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Reparation;